import { ArrowsDownUpIcon } from "@phosphor-icons/react/dist/csr/ArrowsDownUp";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { FilterChip } from "@volca/ui/components/ui/filter-chip";
import { Stack } from "@volca/ui/components/ui/stack";
import type { ReportColumn, ReportSort } from "@/profitability/types";
import { columnLabel } from "./utils";

const SORTS = [
  { id: "value:desc", label: "Largest first" },
  { id: "value:asc", label: "Smallest first" },
  { id: "label:asc", label: "A to Z" },
  { id: "label:desc", label: "Z to A" },
] as const;

// A to Z means nothing for dates, so a date breakdown says it in time.
const DATE_SORTS = [
  { id: "value:desc", label: "Largest first" },
  { id: "value:asc", label: "Smallest first" },
  { id: "label:asc", label: "Oldest first" },
  { id: "label:desc", label: "Newest first" },
] as const;

export const sortId = (sort: ReportSort) => `${sort.by}:${sort.direction}`;

/**
 * What a reader can change about a chart: which field it breaks down by, when
 * the script offers more than one, and the order its bars or slices come in.
 * Both wear the same chip as the filters above, so one page speaks one way.
 */
export const ChartControls = ({
  breakdowns,
  field,
  onFieldChange,
  regroupable = true,
  labelColumn,
  sort,
  onSortChange,
}: {
  breakdowns: ReportColumn[];
  field: string;
  onFieldChange: (field: string) => void;
  /**
   * False while a group control decides the breakdown: the picker would
   * change nothing, so it is left out and only the order is offered.
   */
  regroupable?: boolean;
  /**
   * The column the points are named by, which is what the order is said in.
   * That is the breakdown unless a group control replaced it; none when the
   * points are named by several fields at once.
   */
  labelColumn?: ReportColumn;
  sort: ReportSort;
  onSortChange: (sort: ReportSort) => void;
}) => {
  // Never assert this: a chart whose column has gone from its dataset should
  // lose its control, not the page it is on.
  const current =
    breakdowns.find((item) => item.key === field) ?? breakdowns[0];
  if (!current) return null;
  const sorts = labelColumn?.type === "date" ? DATE_SORTS : SORTS;
  return (
    <Stack direction="row" align="center" gap={1} className="flex-none">
      {regroupable && breakdowns.length > 1 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <FilterChip
                label="By"
                value={columnLabel(current)}
                active={false}
                size="sm"
              />
            }
          />
          <DropdownMenuContent align="end" className="min-w-48">
            <DropdownMenuRadioGroup value={field} onValueChange={onFieldChange}>
              <DropdownMenuLabel>Break down by</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {breakdowns.map((column) => (
                <DropdownMenuRadioItem
                  closeOnClick
                  key={column.key}
                  value={column.key}
                >
                  {columnLabel(column)}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <FilterChip
              label="Order"
              aria-label="Sort this chart"
              value={sorts.find((option) => option.id === sortId(sort))?.label}
              icon={<ArrowsDownUpIcon className="size-3.5 text-subtle" />}
              active={false}
              size="sm"
            />
          }
        />
        <DropdownMenuContent align="end" className="min-w-44">
          <DropdownMenuRadioGroup
            value={sortId(sort)}
            onValueChange={(value) => {
              const [by, direction] = value.split(":");
              onSortChange({
                by: by as ReportSort["by"],
                direction: direction as ReportSort["direction"],
              });
            }}
          >
            <DropdownMenuLabel>Order</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {sorts.map((option) => (
              <DropdownMenuRadioItem
                closeOnClick
                key={option.id}
                value={option.id}
              >
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </Stack>
  );
};
